import {
  afterNextRender,
  Directive,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
} from '@angular/core';

/**
 * Scrolls the host element into view when its bound expanded state transitions
 * to `true`. Used on collapsible section cards so that expanding a section that
 * sits below (or above) the current viewport brings it into view instead of
 * leaving the freshly-revealed content off-screen.
 *
 * Behaviour:
 *  - No-op on the initial render (so a section that starts expanded does not
 *    yank the page on load) and on every collapse.
 *  - On a `false -> true` transition, defers the scroll via `afterNextRender`
 *    so the expanded body is in the DOM (and measured) before scrolling —
 *    matching the OnPush-safe pattern already used for jump-to-invoice.
 *  - Uses `block: 'nearest'`, so an already-visible section is not scrolled;
 *    only out-of-view sections move. `scroll-margin-top` keeps the section
 *    clear of the sticky titlebar + hero, and `scroll-margin-bottom` keeps it
 *    clear of the sticky footer (`spike-footer`, h-8) — without the bottom
 *    margin `scrollIntoView` ignores the sticky footer and leaves the last
 *    rows hidden behind it (and `nearest` may not scroll at all).
 *
 * Reusable across every collapsible-section page (invoice, reachabilities,
 * notifications & forwardings, communication, …).
 *
 * @example
 * <section [cmtRevealOnExpand]="!isCollapsed('invoices')"> … </section>
 */
@Directive({
  selector: '[cmtRevealOnExpand]',
  standalone: true,
  host: {
    '[style.scroll-margin-top.px]': 'scrollMargin()',
    '[style.scroll-margin-bottom.px]': 'scrollMarginBottom()',
  },
})
export class RevealOnExpandDirective {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  /** Expanded state of the section. The scroll fires on `false -> true` only. */
  readonly expanded = input.required<boolean>({ alias: 'cmtRevealOnExpand' });

  /**
   * Top offset (px) kept clear of sticky headers when the section aligns to the
   * top. Default clears the 50px titlebar plus a sticky hero card. Bind
   * `[scrollMargin]` to override per page (different sticky-hero heights).
   */
  readonly scrollMargin = input<number>(170);

  /**
   * Bottom offset (px) kept clear of the sticky footer so the revealed section's
   * last rows are not hidden behind it. Default clears the `h-8` (32px) footer
   * plus a small gap. Bind `[scrollMarginBottom]` to override.
   */
  readonly scrollMarginBottom = input<number>(48);

  private initialised = false;

  constructor() {
    effect(() => {
      const isExpanded = this.expanded();
      // Skip the very first run: a section rendered already-expanded must not
      // scroll the page on load.
      if (!this.initialised) {
        this.initialised = true;
        return;
      }
      if (!isExpanded) return;
      afterNextRender(
        () =>
          this.elementRef.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          }),
        { injector: this.injector },
      );
    });
  }
}
